import { useModal, useOverlay, usePreventScroll } from '@react-aria/overlays';
import { mergeProps, useViewportSize } from '@react-aria/utils';
import type { ModalProps } from '@react-types/overlays';
import type { DOMRef } from '@react-types/shared';
import classNames from 'classnames';
import type { HTMLAttributes, ReactNode, RefObject } from 'react';
import React, { forwardRef } from 'react';
import { useDOMRef } from '../../../utils';
import { Overlay } from '../controls/overlay';
import { Underlay } from '../controls/underlay';

interface ModalWrapperProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  isOpen?: boolean;
  onClose?: () => void;
  type?: 'modal' | 'fullscreen' | 'fullscreenTakeover';
  overlayProps: HTMLAttributes<HTMLElement>;
}

const Modal = (props: ModalProps, ref: DOMRef<HTMLDivElement>) => {
  let { children, onClose, type, ...otherProps } = props;
  let domRef = useDOMRef(ref);

  let { overlayProps, underlayProps } = useOverlay(props, domRef);

  return (
    <Overlay {...otherProps}>
      <Underlay {...underlayProps} />
      <ModalWrapper
        className={props.UNSAFE_className}
        style={props.UNSAFE_style}
        onClose={onClose}
        type={type}
        ref={domRef}
        overlayProps={overlayProps}
      >
        {children}
      </ModalWrapper>
    </Overlay>
  );
};

let typeMap = {
  fullscreen: 'fullscreen',
  fullscreenTakeover: 'fullscreenTakeover'
};

let ModalWrapper = forwardRef((props: ModalWrapperProps, ref: RefObject<HTMLDivElement>) => {
  let { children, isOpen, type, overlayProps, ...otherProps } = props;
  let typeVariant = typeMap[type];

  usePreventScroll();
  let { modalProps } = useModal();

  let wrapperClassName = classNames('modal-wrapper-style', classNames('overrideStyles'));

  let modalClassName = classNames(
    'modalStyles',
    {
      'is-open-class': isOpen
    },
    classNames('overrideStyles'),
    { [`modal--${typeVariant}`]: typeVariant },
    otherProps.className as any
  );

  let viewport = useViewportSize();
  let style: any = {
    minHeight: viewport.height + 'px'
  };

  return (
    <div className={wrapperClassName} style={style}>
      <div
        {...mergeProps(otherProps, overlayProps, modalProps)}
        ref={ref}
        className={modalClassName}
        data-testid="modal"
      >
        {children}
      </div>
    </div>
  );
});

let _Modal = forwardRef(Modal);
_Modal.displayName = 'Modal';
export { _Modal as Modal };
